import _get from 'lodash/get';
import {fetchPrice, fetchPriceFulfilled, fetchPriceRejected} from './actions';
import {defaultPricingParams} from './constants';
import * as api from './api';
import {IWHIJoinState} from '../store';
import {Scale} from '@nib/phi-constants';
import {isAxiosError} from '../types';
import {getDefaultRateCode} from '../ppcConfig/thunks';
import {FetchPricePayload} from './types';
import {addYears, format} from 'date-fns';

//This code is needed as the scale can be set via quote cookie to 'Family' before the customer
//has had a change to add dependants and we want to quote a price on before they do
const getNumberOfChildren = (session) => {
  const numberOfAddedDependants = _get(session, 'personalDetails.dependants', []).length;
  const scale = _get(session, 'personalDetails.scale', defaultPricingParams.scale);
  if (numberOfAddedDependants === 0 && scale === Scale.Family) {
    return 1;
  }
  return numberOfAddedDependants;
};

const getEnrolDetails = (session) => {
  const sourceId = _get(session, 'enrolDetails.sourceId');
  const sourceType = _get(session, 'enrolDetails.sourceType');
  if (sourceId && sourceType) {
    return '' + sourceType + sourceId;
  }
  return null;
};

//If the cover start date is set and visa end date is not we want to set the visa end date to 1 year from the cover start date
const calculateVisaEndDate = (session) => {
  const coverStartDate = _get(session, 'financialDetails.coverStartDate.date');
  const visaEndDate = _get(session, 'visaDetails.expectedVisaEndDate');
  if (!coverStartDate && !visaEndDate) {
    return defaultPricingParams.endDate;
  } else if (coverStartDate && !visaEndDate) {
    return format(addYears(new Date(coverStartDate), 1), 'yyyy-MM-dd');
  }
  return visaEndDate;
};

export const getPrice = () => async (dispatch, getState) => {
  const iwhiJoinState: IWHIJoinState = getState();
  const session = iwhiJoinState.session.session;
  const companyRateCode = _get(session, 'company.rateCode');
  const offerRateCode = _get(iwhiJoinState.offer, 'offer.rateCode');
  const defaultRateCode = _get(iwhiJoinState, 'ppcConfigData.rateCode');
  const scale = _get(session, 'personalDetails.scale', defaultPricingParams.scale);
  const numberOfChildren = getNumberOfChildren(session);

  //This logic is needed as we can't send up a default partner dob for 'single parent couple' or 'Single' to the pricing api
  //as partnerDob is used to count the number of people on the policy and would cause there to be 3 people on a couple scale causing an error
  //We want to have a default partner dob for 'normal Couple' or 'Family' so they can be quoted as base price for that scale before entering partner or dependant details
  let partnerDob: any;
  if (scale !== Scale.Single && !(scale === Scale.Couple && numberOfChildren === 1)) {
    partnerDob = _get(session, 'personalDetails.partner.dateOfBirth') || defaultPricingParams.dob;
  }

  const mappedPricingParameters: FetchPricePayload = {
    dateJoinedFund: _get(session, 'financialDetails.coverStartDate.date') || defaultPricingParams.dateJoinedFund,
    dob: _get(session, 'personalDetails.policyHolder.dateOfBirth') || defaultPricingParams.dob,
    effectiveDate: _get(session, 'financialDetails.coverStartDate.date') || defaultPricingParams.effectiveDate,
    endDate: calculateVisaEndDate(session),
    excess: _get(session, 'productSelection.hospital.excess')?.toString() || defaultPricingParams.excess,
    gender: _get(session, 'personalDetails.policyHolder.gender') || defaultPricingParams.gender,
    hospitalProduct: _get(session, 'productSelection.hospital.id') || defaultPricingParams.hospitalProduct,
    extrasProduct: _get(session, 'productSelection.extras.id') || undefined,
    paymentFrequency: _get(session, 'financialDetails.paymentFrequency') || defaultPricingParams.paymentFrequency,
    scale: scale,
    startDate: _get(session, 'financialDetails.coverStartDate.date') || defaultPricingParams.startDate,
    partnerDob: partnerDob,
    numberOfChildren: numberOfChildren,
    rate: companyRateCode || offerRateCode || defaultRateCode,
    source: getEnrolDetails(session)
  };

  //There must be a rate passed to pricing api for every call, if not make separate call to fetch each and wait on the result b4 making pricing call ??
  if (!mappedPricingParameters.rate) {
    mappedPricingParameters.rate = await getDefaultRateCode();
  }

  const apiParameters = {
    ...mappedPricingParameters
  };

  dispatch(fetchPrice(apiParameters));

  try {
    const pricingResponse = await api.fetch(apiParameters);
    if (pricingResponse && !isAxiosError(pricingResponse)) {
      dispatch(fetchPriceFulfilled(pricingResponse.data));
      return pricingResponse;
    } else {
      dispatch(fetchPriceRejected({error: pricingResponse.data}));
    }
  } catch (exception) {
    dispatch(fetchPriceRejected(exception));
  }
  return undefined;
};

export const pricingObserverPriceFetch = () => async (dispatch) => {
  dispatch({type: 'pricingObserver/fetchPrice'});
  dispatch(getPrice());
};
